<template>
    <v-container>
        <h2 class="mb-4">{{ mode === 'create' ? 'Create Court' : mode === 'edit' ? 'Edit Court' : 'View Court' }}</h2>
        <div v-if="mode && !['edit', 'create'].includes(mode)" class="edit-button-wrapper">
            <v-btn class="mx-2" dark medium color="primary" :loading="navigating" :disabled="navigating || submitting || deletingImage" @click="onEdit">
                <v-icon dark>mdi-account-edit</v-icon>
            </v-btn>
        </div>
        <v-form :readonly="readOnly">
            <v-text-field
                v-model="court.title"
                :rules="rules"
                label="Title"
                :clearable="clearable"
            />
            <v-select
                v-model="court.sport"
                :items="sports"
                item-text="title"
                item-value="id"
                return-object
                label="Sport"
                clearable
            />
            <v-file-input
                v-if="mode !== 'show'"
                v-model="newImage"
                label="Court Image"
                :rules="imageRules"
                filled
                show-size
                prepend-icon="mdi-camera"
                @change="createNewImagePreview"
            />
            <div v-if="previewImage && !previewImage.shouldDelete" class="image-input" :class="{'green-border': !previewImage.id}">
                <div v-if="mode === 'edit'" class="delete-btn">
                    <v-btn class="mx-2 white--text" small color="secondary" :loading="deletingImage" :disabled="deletingImage || submitting || navigating" @click="onImageDelete">
                        X
                    </v-btn>
                </div>
                <v-img v-if="previewImage.url" class="img-preview" :src="previewImage.url" contain height="180" />
            </div>
        </v-form>
        <div v-if="mode !== 'show'" class="submit-button-wrapper">
            <v-btn class="mx-2 white--text" :loading="submitting" :disabled="shouldDisableSubmit() || submitting || navigating || deletingImage" large color="primary" @click="onSubmit">
                Submit
            </v-btn>
        </div>
    </v-container>
</template>

<script>
    import courtService from '@/services/court-service'
    import imageService from '@/services/image-service'
    import sportService from '@/services/sport-service'

    export default {
        name: 'CourtForm',
        data: () => {
            return {
                mode: null,
                readOnly: false,
                clearable: true,
                apiUrl: process.env.VUE_APP_API_HOST_DOMAIN,
                court: {
                    title: '',
                    sport: null
                },
                courtId: null,
                sports: [],
                rules: [
                    v => !!v || 'Title is required'
                ],
                newImage: null,
                previewImage: null,
                imageRules: [
                    value => !value || value.size < 2000000 || 'Image size should be less than 2 MB!'
                ],
                submitting: false,
                navigating: false,
                deletingImage: false
            }
        },
        created() {
            this.mode = this.$route.params.mode
            this.loadSports()
            if (this.mode === 'create') {
                return
            } else if (this.mode === 'show') {
                this.readOnly = true
                this.clearable = false
            }
            this.courtId = this.$route.params.courtId
            if (this.courtId) {
                courtService.getCourtById(this.courtId).then(resp => {
                    this.court = resp.data.court
                    this.previewImage = this.court.image
                    if (this.previewImage) {
                        console.log('previewImage', process.env.VUE_APP_GOOGLE_BUCKET_URL + this.previewImage.imageName)
                        this.previewImage.url = process.env.VUE_APP_GOOGLE_BUCKET_URL + this.previewImage.imageName
                    }
                })
            }
        },
        methods: {
            loadSports() {
                sportService.getAllSports().then(resp => {
                    this.sports = resp.data.data || []
                })
            },
            shouldDisableSubmit() {
                return !this.court.title
            },
            async onEdit() {
                this.navigating = true
                try {
                    await this.$router.push(`/courts/${this.courtId}/edit`)
                } finally {
                    this.navigating = false
                }
            },
            async createCourt() {
                const formData = new FormData()
                formData.append('title', this.court.title)
                if (this.court.sport) {
                    formData.append('sportId', this.court.sport.id)
                }
                if (this.hasSelectedImage(this.newImage)) {
                    formData.append('file', this.newImage)
                }
                const response = await courtService.createCourt(formData)
                if (response.data.id) {
                    this.courtId = response.data.id
                }
                this.$router.push(`/courts`)
            },
            async editCourt() {
                const formData = new FormData()
                formData.append('title', this.court.title)
                if (this.court.sport) {
                    formData.append('sportId', this.court.sport.id)
                } else {
                    formData.append('sportId', '')
                }
                if (this.hasSelectedImage(this.newImage)) {
                    formData.append('file', this.newImage)
                }
                await courtService.editCourtById(this.courtId, formData)
                this.$router.push(`/courts`)
            },
            async onSubmit() {
                this.submitting = true
                try {
                    if (this.mode === 'edit') {
                        await this.editCourt()
                    } else if (this.mode === 'create') {
                        await this.createCourt()
                    }
                    await this.deleteCourtImage()
                } finally {
                    this.submitting = false
                }
            },
            async deleteCourtImage() {
                if (this.previewImage && this.previewImage.id && this.previewImage.shouldDelete) {
                    await imageService.deleteImageById(this.previewImage.id)
                    this.previewImage = null
                }
            },
            createNewImagePreview() {
                if (!this.hasSelectedImage(this.newImage)) {
                    this.previewImage = null
                    return
                }
                this.previewImage = this.newImage
                this.previewImage.url = URL.createObjectURL(this.newImage)
            },
            onImageDelete() {
                this.deletingImage = true
                if (this.previewImage && this.previewImage.id) {
                    this.previewImage.shouldDelete = true
                    this.previewImage.url = null
                    this.previewImage = { ...this.previewImage }
                } else {
                    this.newImage = null
                }
                this.deletingImage = false
            },
            hasSelectedImage(image) {
                return image && typeof image === 'object' && image.size !== undefined && image.name
            }
        }
    }
</script>

<style scoped>
.edit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.submit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 2vh;
}

.image-input {
    border: 2px dashed #ccc;
    padding: 20px;
    border-radius: 4px;
    margin-top: 20px;
    position: relative;
}

.image-input.green-border {
    border-color: #4caf50;
}

.delete-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
}

.img-preview {
    width: 100%;
    max-width: 100%;
    height: 180px;
    margin: 0 auto 10px;
}
</style>


